import React, { useEffect, useRef } from 'react'
import { gsap, Power4 } from 'gsap'
import Slider from 'react-slick'
import styled from 'styled-components'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import styles from './EventSlider.module.scss'

const formatDate = (dateStr) => {
  const date = new Date(dateStr)
  const daysOfWeek = ['日', '月', '火', '水', '木', '金', '土']
  const dayOfWeek = daysOfWeek[date.getDay()]
  return {
    fullDate: `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${dayOfWeek}）`,
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    dayOfWeek: dayOfWeek,
  }
}

const getEventDateDisplay = (post) => {
  const startInfo = post.start_date ? formatDate(post.start_date) : null
  const endInfo = post.end_date ? formatDate(post.end_date) : null

  if (startInfo && endInfo) {
    if (startInfo.fullDate === endInfo.fullDate) {
      return startInfo.fullDate
    } else if (startInfo.year === endInfo.year) {
      if (startInfo.month === endInfo.month) {
        return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.day}日（${endInfo.dayOfWeek}）`
      } else {
        return `${startInfo.year}年${startInfo.month}月${startInfo.day}日（${startInfo.dayOfWeek}）〜${endInfo.month}月${endInfo.day}日（${endInfo.dayOfWeek}）`
      }
    } else {
      return `${startInfo.fullDate}〜${endInfo.fullDate}`
    }
  } else {
    return startInfo?.fullDate || endInfo?.fullDate || ''
  }
}

const EventSlider = ({ activeEvents }) => {
  const sectionRef = useRef(null)
  const titleRef = useRef(null)

  useEffect(() => {
    if (sectionRef.current && titleRef.current) {
      gsap.set([titleRef.current, sectionRef.current], { opacity: 0, y: 50 })
      gsap.to([titleRef.current, sectionRef.current], {
        opacity: 1,
        y: 0,
        duration: 1,
        ease: Power4.easeOut,
        delay: 0.6,
      })
    }
  }, [])

  const settings = {
    dots: true,
    infinite: activeEvents.length > 3,
    autoplaySpeed: 8000,
    slidesToShow: 3,
    initialSlide: 0,
    autoplay: true,
    arrows: false,
    draggable: true,
    appendDots: (dots) => <ul> {dots} </ul>,
    responsive: [
      {
        breakpoint: 819,
        settings: {
          slidesToShow: 1,
          centerMode: true,
          centerPadding: '51px',
          swipe: true,
        },
      },
    ],
  }

  return (
    <SliderWrapper className={styles.slider}>
      <h2 className={styles.eventlist} ref={titleRef} id="pgTarget">
        開催予定のイベント・セミナー
      </h2>
      <div className={styles.eventlist_container} ref={sectionRef}>
        {activeEvents.length > 0 ? (
          <Slider {...settings}>
            {activeEvents.map((event) => (
              <a href={`/events/${event.id}`} key={event.id}>
                <div
                  className={`${styles.eventlist_item} ${
                    activeEvents.length === 1 ? styles.eventlist_item_noslide : ''
                  }`}
                >
                  <figure className={styles.eventlist_item_img}>
                    <img
                      src={event.thumbnail_img || '/images/img/img_thumbnail_dummy.png'}
                      alt={event.title.rendered || 'デフォルト画像'}
                    />
                  </figure>
                  <p className={styles.eventlist_item_date}>{getEventDateDisplay(event)}</p>
                  <h3 className={styles.eventlist_item_title}>{event.title.rendered}</h3>
                </div>
              </a>
            ))}
          </Slider>
        ) : (
          <p className={styles.noEvents}>開催予定のイベント・セミナーはありません</p>
        )}
      </div>
    </SliderWrapper>
  )
}

const SliderWrapper = styled.div`
  .slick-slider {
    display: none;
  }

  .slick-initialized {
    display: block;
  }

  .slick-dots {
    margin: 20px 0 0;
    position: relative;
    width: auto;
    bottom: 0;
    text-align: left;
    @media (min-width: 720px) and (max-width: 819px) {
      margin-left: 40px;
    }
    @media (max-width: 719px) {
      margin-left: 24px;
    }
  }

  .slick-track {
    margin-left: 0;
    @media (max-width: 819px) {
      margin-left: auto;
    }
  }

  .slick-dots li {
    position: relative;
    width: 40px;
    height: 10px;
    margin: 0 0 0 8px;
    &::after {
      content: '';
      position: absolute;
      width: 100%;
      height: 4px;
      top: 0;
      bottom: 0;
      margin: auto 0;
      background: #ccc;
    }
    @media (max-width: 819px) {
      width: 32px;
    }
  }

  .slick-dots li:first-child {
    margin: 0;
  }

  .slick-dots li.slick-active::after {
    background: #db0032;
  }

  .slick-dots li button {
    width: 100%;
    height: 100%;
    padding: 0;
    background: transparent;
  }

  .slick-dots li button::before {
    content: none;
  }
`

export default EventSlider
